import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import { ExclamationCircleIcon, TrashIcon } from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { getRooms } from "../../slices/rooms";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import TextInput from "../forms/TextInput";
import {
  ContactBookState,
  createContactBook,
  deleteContactBooks,
  getContactBooks,
  updateContactBook,
} from "../../slices/communication";
import HorizontalRadio from "../forms/HorizontalRadio";
import Card from "../layout/Card";
import { onlyUnique } from "../../utils/arrays";
import { checkE164Format } from "../../utils/validation";
import DisabledTextInput from "../forms/DisabledTextInput";
import DangerZone from "../forms/DangerZone";

export default function ContactBooks() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getContactBooks());
    dispatch(getRooms());
  }, [dispatch]);

  // Set state
  const [selectedContactBook, setSelectedContactBook] =
    useState<ContactBookState | null>(null);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [createAddressBookForm, setCreateAddressBookForm] = useState<{
    name: string;
    contacts: any[];
  }>({
    name: "",
    contacts: [
      {
        id: 0,
        name: "",
        type: "Alexa",
        room: "",
        phone_numbers: "",
      },
    ],
  });

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { contact_books: contactBooks, options } = useAppSelector(
    (state) => state.communication,
  );
  const { rooms } = useAppSelector((state) => state.rooms);
  const language = user === null ? "UK" : user.language;

  // Functions
  function openCreateModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  function selectContactBookToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [contactBookId] = args;
    const contactBooksData =
      contactBooks.data === null
        ? null
        : contactBooks.data.filter((book) => book.id === contactBookId)[0];
    setSelectedContactBook(contactBooksData);
    setShowEditModal(true);
  }

  async function confirmCreateContactBook(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "warning"),
      );
      return;
    }

    const { name, contacts } = createAddressBookForm;
    if (name === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "warning", 3000),
      );
    }

    // Validate contacts
    const formattedContacts = [];
    for (let i = 0; i < contacts.length; i += 1) {
      const candidate = contacts[i];

      const objType = Object.keys(options.contact_type).filter(
        (x) => options.contact_type[x][language] === candidate.type,
      );
      if (objType.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("invalid_contact_type", language),
            "warning",
          ),
        );
        return;
      }

      const obj: any = {
        name: candidate.name,
        type: objType[0],
        room: candidate.room,
        detail: {},
        contact_id: candidate.contact_id,
      };

      // Validate room
      if (obj.type === "A" && obj.room.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("invalid_room_contact", language),
            "warning",
          ),
        );
        return;
      }

      if (obj.type === "A")
        obj.detail.alexa_commmunication_profile_id =
          candidate.alexa_communication_profile_id;

      if (obj.type === "P") {
        const cleanedNumbers = candidate.phone_numbers
          .replace(" ", "")
          .split(",")
          .map((x: string) => x.trim())
          .filter((x: string) => x.length > 0);
        const uniqueNumbers = cleanedNumbers.filter(onlyUnique);
        if (uniqueNumbers.length > 3) {
          dispatch(
            addAlert(
              generateAlertText("too_many_phone_numbers", language),
              "warning",
            ),
          );
          return;
        }
        if (!checkE164Format(uniqueNumbers)) {
          dispatch(
            addAlert(
              generateAlertText("phone_numbers_dont_match_e164", language),
              "warning",
            ),
          );
          return;
        }
        obj.detail.phone_numbers = uniqueNumbers;
      }
      formattedContacts.push(obj);
    }

    setShowCreateModal(false);
    const res: any = await dispatch(
      createContactBook({
        name,
        property: user.property.id,
        contacts: formattedContacts,
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(
          generateAlertText("create_contact_book", language),
          "success",
          3000,
        ),
      );
      setCreateAddressBookForm({ name: "", contacts: [] });
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditContactBook(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "warning"),
      );
      return;
    }
    if (selectedContactBook === null) {
      dispatch(
        addAlert(
          generateAlertText("missing_contact_book", language),
          "warning",
        ),
      );
      return;
    }

    const { id, name, contacts } = selectedContactBook;
    if (name === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "warning", 3000),
      );
    }

    // Validate contacts
    const formattedContacts = [];
    for (let i = 0; i < contacts.length; i += 1) {
      const candidate = contacts[i];

      const objType = Object.keys(options.contact_type).filter(
        (x) => options.contact_type[x][language] === candidate.type,
      );
      if (objType.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("invalid_contact_type", language),
            "warning",
          ),
        );
        return;
      }

      const obj: any = {
        name: candidate.name,
        type: objType[0],
        room: candidate.room,
        detail: {},
        contact_id: candidate.contact_id,
      };

      // Validate room
      if (obj.type === "A" && obj.room.length === 0) {
        dispatch(
          addAlert(
            generateAlertText("invalid_room_contact", language),
            "warning",
          ),
        );
        return;
      }

      if (obj.type === "A")
        obj.detail.alexa_commmunication_profile_id =
          candidate.alexa_communication_profile_id;

      if (obj.type === "P") {
        const cleanedNumbers = candidate.phone_numbers
          .replace(" ", "")
          .split(",")
          .map((x: string) => x.trim())
          .filter((x: string) => x.length > 0);
        const uniqueNumbers = cleanedNumbers.filter(onlyUnique);
        if (uniqueNumbers.length > 3) {
          dispatch(
            addAlert(
              generateAlertText("too_many_phone_numbers", language),
              "warning",
            ),
          );
          return;
        }
        if (!checkE164Format(uniqueNumbers)) {
          dispatch(
            addAlert(
              generateAlertText("phone_numbers_dont_match_e164", language),
              "warning",
            ),
          );
          return;
        }
        obj.detail.phone_numbers = uniqueNumbers;
      }
      formattedContacts.push(obj);
    }

    setShowEditModal(false);
    const res: any = await dispatch(
      updateContactBook({
        contactBookId: id,
        name,
        property: user.property.id,
        contacts: formattedContacts,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_contact_book", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  function increaseContactCount(e: any, args: any[]) {
    e.preventDefault();
    const currentNumContacts = createAddressBookForm.contacts.length;
    const currentContacts = [...createAddressBookForm.contacts];
    const updatedContacts = [
      ...currentContacts,
      {
        id: currentNumContacts,
        name: "",
        type: "Alexa",
        room: "",
        phone_numbers: "",
      },
    ];
    setCreateAddressBookForm({
      ...createAddressBookForm,
      contacts: updatedContacts,
    });
  }

  function increaseSelectedContactCount(e: any, args: any[]) {
    e.preventDefault();
    if (selectedContactBook === null) return;
    const currentNumContacts = selectedContactBook.contacts.length;
    const currentContacts = [...selectedContactBook.contacts];
    const updatedContacts = [
      ...currentContacts,
      {
        id: currentNumContacts,
        name: "",
        type: "Alexa",
        room: "",
        phone_numbers: "",
      },
    ];
    setSelectedContactBook({
      ...selectedContactBook,
      contacts: updatedContacts,
    });
  }

  function createCardText(contactBookId: string) {
    if (contactBooks.data === null) return null;
    const contactBookData = contactBooks.data.filter(
      (book) => book.id === contactBookId,
    )[0];
    const value = contactBookData.contacts.length;
    return `${value} ${
      value === 1
        ? translateText("contact_lower", language)
        : translateText("contacts_lower", language)
    }`;
  }

  async function confirmDeleteContactBook(e: any, args: any[]) {
    e.preventDefault();
    const [contactBookId] = args;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    if (contactBookId === null || contactBookId === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_contact_book", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(
      deleteContactBooks({ contactBookIds: [contactBookId] }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  // Format rooms data and filter by query param tag
  const contactBooksData = contactBooks.data === null ? [] : contactBooks.data;
  const roomsData = rooms.data === null ? [] : rooms.data;
  const roomChoices = roomsData.map((room) => [room.id, room.name]);
  const contactTypeChoices = [
    ["Alexa", translateText("room", language)],
    ["Phone number", translateText("phone_number", language)],
  ];

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("contact_books_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_contact_book", language)}
              </Button>
            }
          />
        }
      />

      {/* Create contact book modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_contact_book", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateContactBook}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_contact_book", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={createAddressBookForm}
                stateName="name"
                onChange={setCreateAddressBookForm}
              />

              {/* Contacts */}
              {createAddressBookForm.contacts.map((contact, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                >
                  {/* Contact name */}
                  <div>
                    {createAddressBookForm.contacts.length > 1 && (
                      <div className="flex justify-start">
                        <Button
                          type="button"
                          className={`${getButtonStyle(
                            "solid",
                            "danger",
                          )} mb-3`}
                          onClick={(e) => {
                            e.preventDefault();
                            const existingContacts = [
                              ...createAddressBookForm.contacts,
                            ];
                            existingContacts.splice(index, 1);
                            const newContacts = existingContacts.map(
                              (contact, index) => ({ ...contact, id: index }),
                            );
                            setCreateAddressBookForm({
                              ...createAddressBookForm,
                              contacts: newContacts,
                            });
                          }}
                        >
                          <TrashIcon
                            className={`h-5 w-5 text-white`}
                            aria-hidden="true"
                          />
                        </Button>
                      </div>
                    )}
                    <label
                      htmlFor={`contact-${contact}`}
                      className={`mb-3 block text-left text-sm font-medium text-black`}
                    >
                      {`${translateText("contact", language)} ${index + 1}`}
                    </label>
                    <input
                      id={`contact-${contact}`}
                      type="text"
                      className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                      placeholder={translateText(
                        "enter_contact_name",
                        language,
                      )}
                      value={createAddressBookForm.contacts[index].name}
                      required={true}
                      onChange={(e) => {
                        const existingContacts = [
                          ...createAddressBookForm.contacts,
                        ];
                        existingContacts[index].name = e.target.value;
                        setCreateAddressBookForm({
                          ...createAddressBookForm,
                          contacts: existingContacts,
                        });
                      }}
                    />
                  </div>

                  {/* Contact type */}
                  <HorizontalRadio
                    label={translateText("type", language)}
                    small={translateText("contact_room_type", language)}
                    showLabel={true}
                  >
                    {contactTypeChoices.map((choice) => (
                      <div
                        key={`${contact.id}-type-${choice[0]}`}
                        className="flex items-center"
                      >
                        <input
                          id={`${contact.id}-type-${choice[0]}`}
                          name={`${contact.id}-type`}
                          type="radio"
                          defaultChecked={
                            createAddressBookForm.contacts[index].type ===
                            choice[0]
                          }
                          className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                          value={choice[0]}
                          onChange={(e) => {
                            const existingContacts = [
                              ...createAddressBookForm.contacts,
                            ];
                            existingContacts[index].type = e.target.value;
                            setCreateAddressBookForm({
                              ...createAddressBookForm,
                              contacts: existingContacts,
                            });
                          }}
                        />
                        <label
                          htmlFor={`${contact.id}-type-${choice[0]}`}
                          className="ml-3 block text-sm font-medium leading-6 text-black"
                        >
                          {choice[1]}
                        </label>
                      </div>
                    ))}
                  </HorizontalRadio>

                  {/* Contact room */}
                  {contact.type === "Alexa" && (
                    <>
                      <label
                        htmlFor={`contact-${contact.id}-room`}
                        className={`hidden text-left text-sm font-medium text-black`}
                      />
                      <div className="col-span-full">
                        <select
                          id={`contact-${contact.id}-room`}
                          className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          required={true}
                          onChange={(e) => {
                            const existingContacts = [
                              ...createAddressBookForm.contacts,
                            ];
                            existingContacts[index].room = e.target.value;
                            setCreateAddressBookForm({
                              ...createAddressBookForm,
                              contacts: existingContacts,
                            });
                          }}
                          defaultValue=""
                        >
                          <option value="">
                            {translateText("choose_room", language)}
                          </option>
                          {roomChoices.map((choice: any) => (
                            <option key={choice[0]} value={choice[0]}>
                              {choice[1]}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  {/* Contact phone numbers */}
                  {contact.type === "Phone number" && (
                    <div>
                      <p className="text-xs text-left text-secondary italic mb-3">
                        {translateText("phone_numbers_limit", language)}
                      </p>
                      <input
                        id={`contact-${contact.id}-phone_numbers`}
                        type="text"
                        className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                        placeholder={translateText(
                          "enter_phone_numbers",
                          language,
                        )}
                        value={
                          createAddressBookForm.contacts[index].phone_numbers
                        }
                        required={true}
                        onChange={(e) => {
                          const existingContacts = [
                            ...createAddressBookForm.contacts,
                          ];
                          existingContacts[index].phone_numbers =
                            e.target.value;
                          setCreateAddressBookForm({
                            ...createAddressBookForm,
                            contacts: existingContacts,
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              ))}

              <Button
                type="button"
                className={`${getButtonStyle("solid", "primary")}`}
                onClick={increaseContactCount}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("add_another_contact", language)}
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit contact book modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_contact_book", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditContactBook}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-black"
            >
              {`Edit contact book "${selectedContactBook?.name}"`}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Created by */}
              <DisabledTextInput
                id="created_by"
                label={translateText("created_by", language)}
                className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                showLabel={true}
                value={
                  selectedContactBook === null
                    ? ""
                    : selectedContactBook.created_by
                }
              />

              {/* Updated by */}
              {selectedContactBook !== null &&
                selectedContactBook.last_updated_by !== null && (
                  <DisabledTextInput
                    id="last_updated_by"
                    label={translateText("updated_by", language)}
                    className="block w-full appearance-none rounded-md border-none bg-transparent px-3 py-2 text-secondary sm:text-sm"
                    showLabel={true}
                    value={
                      selectedContactBook === null
                        ? ""
                        : selectedContactBook.last_updated_by
                    }
                  />
                )}

              {/* Name */}
              <TextInput
                id="name"
                type="text"
                label={translateText("name", language)}
                className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedContactBook}
                stateName="name"
                onChange={setSelectedContactBook}
              />

              {/* Contacts */}
              {selectedContactBook !== null &&
                selectedContactBook.contacts.map((contact, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                  >
                    {/* Contact name */}
                    <div>
                      {selectedContactBook.contacts.length > 1 && (
                        <div className="flex justify-start">
                          <Button
                            type="button"
                            className={`${getButtonStyle(
                              "solid",
                              "danger",
                            )} mb-3`}
                            onClick={(e) => {
                              e.preventDefault();
                              const existingContacts = [
                                ...selectedContactBook.contacts,
                              ];
                              existingContacts.splice(index, 1);
                              const newContacts = existingContacts.map(
                                (contact, index) => ({ ...contact, id: index }),
                              );
                              setSelectedContactBook({
                                ...selectedContactBook,
                                contacts: newContacts,
                              });
                            }}
                          >
                            <TrashIcon
                              className={`h-5 w-5 text-white`}
                              aria-hidden="true"
                            />
                          </Button>
                        </div>
                      )}
                      <label
                        htmlFor={`contact-${contact}`}
                        className={`mb-3 block text-left text-sm font-medium text-black`}
                      >
                        {`${translateText("contact", language)} ${index + 1}`}
                      </label>
                      <input
                        id={`contact-${contact}`}
                        type="text"
                        className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                        placeholder={translateText(
                          "enter_contact_name",
                          language,
                        )}
                        value={selectedContactBook.contacts[index].name}
                        required={true}
                        onChange={(e) => {
                          const existingContacts = [
                            ...selectedContactBook.contacts,
                          ];
                          existingContacts[index].name = e.target.value;
                          setSelectedContactBook({
                            ...selectedContactBook,
                            contacts: existingContacts,
                          });
                        }}
                      />
                    </div>

                    {/* Contact type */}
                    <HorizontalRadio
                      label={translateText("type", language)}
                      small={translateText("contact_room_type", language)}
                      showLabel={true}
                    >
                      {contactTypeChoices.map((choice) => (
                        <div
                          key={`${contact.id}-type-${choice[0]}`}
                          className="flex items-center"
                        >
                          <input
                            id={`${contact.id}-type-${choice[0]}`}
                            name={`${contact.id}-type`}
                            type="radio"
                            defaultChecked={
                              selectedContactBook.contacts[index].type ===
                              choice[0]
                            }
                            className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                            value={choice[0]}
                            onChange={(e) => {
                              const existingContacts = [
                                ...selectedContactBook.contacts,
                              ];
                              existingContacts[index].type = e.target.value;
                              setSelectedContactBook({
                                ...selectedContactBook,
                                contacts: existingContacts,
                              });
                            }}
                          />
                          <label
                            htmlFor={`${contact.id}-type-${choice[0]}`}
                            className="ml-3 block text-sm font-medium leading-6 text-black"
                          >
                            {choice[1]}
                          </label>
                        </div>
                      ))}
                    </HorizontalRadio>

                    {/* Contact room */}
                    {contact.type === "Alexa" && (
                      <>
                        <label
                          htmlFor={`contact-${contact.id}-room`}
                          className={`hidden text-left text-sm font-medium text-black`}
                        />
                        <div className="col-span-full">
                          <select
                            id={`contact-${contact.id}-room`}
                            className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            onChange={(e) => {
                              const existingContacts = [
                                ...selectedContactBook.contacts,
                              ];
                              existingContacts[index].room = e.target.value;
                              setSelectedContactBook({
                                ...selectedContactBook,
                                contacts: existingContacts,
                              });
                            }}
                            defaultValue={
                              contact.room === null ? "" : contact.room
                            }
                          >
                            <option value="">
                              {translateText("choose_room", language)}
                            </option>
                            {roomChoices.map((choice: any) => (
                              <option key={choice[0]} value={choice[0]}>
                                {choice[1]}
                              </option>
                            ))}
                          </select>
                        </div>
                      </>
                    )}

                    {/* Contact phone numbers */}
                    {contact.type === "Phone number" && (
                      <div>
                        <p className="text-xs text-left text-secondary italic mb-3">
                          {translateText("phone_numbers_limit", language)}
                        </p>
                        <input
                          id={`contact-${contact.id}-phone_numbers`}
                          type="text"
                          className="block w-full appearance-none rounded-md border border-secondary/40 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          placeholder={translateText(
                            "enter_phone_numbers",
                            language,
                          )}
                          value={
                            selectedContactBook.contacts[index].phone_numbers
                          }
                          required={true}
                          onChange={(e) => {
                            const existingContacts = [
                              ...selectedContactBook.contacts,
                            ];
                            existingContacts[index].phone_numbers =
                              e.target.value;
                            setSelectedContactBook({
                              ...selectedContactBook,
                              contacts: existingContacts,
                            });
                          }}
                        />
                      </div>
                    )}
                  </div>
                ))}

              <Button
                type="button"
                className={`${getButtonStyle("solid", "primary")}`}
                onClick={increaseSelectedContactCount}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("add_another_contact", language)}
              </Button>

              {/* Danger zone */}
              {selectedContactBook !== null && (
                <DangerZone
                  objectId={selectedContactBook.id}
                  handler={confirmDeleteContactBook}
                  language={language}
                />
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {contactBooks.loading || rooms.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {contactBooksData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {contactBooksData.map((book) => (
                    <a
                      href="#!"
                      key={book.id}
                      onClick={(e) => selectContactBookToEdit(e, [book.id])}
                    >
                      <Card
                        key={book.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {book.name}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2">
                          <p className={`text-sm text-secondary`}>
                            {createCardText(book.id)}
                          </p>
                        </div>
                      </Card>
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_contact_books", language)}
                subtext={translateText("no_contact_books_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
